import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@express-labs/raven-tools';
import {
  Blurb,
  Button,
  FloatingInput,
  FloatingPasswordInput,
  Loader,
  Notification,
} from '@express-labs/raven-ui';
import { Formik, Form, Field } from 'formik';
import Recaptcha from 'react-google-recaptcha';

import ThirdPartyScriptLoader from '../../../../components/ThirdPartyScriptLoader';
import { CAPTCHA_KEY, GOOGLE_RECAPTCHA_ENTERPRISE_KEY } from '../../../../constants';
import { validateEmail, validatePassword } from './formValidation';
import SlotNotificationAuthorable from '../../../../components/SlotNotificationAuthorable';

import s from './LoginForm.scss';

function LoginForm(props) {
  const {
    className,
    handleFormSubmit,
    captchaError,
    hasSignInError,
    captchaExpiredCallback,
    captchaVerifiedCallback,
    captchaInputRef,
    showCaptcha,
    isFetching,
    isRecaptchaEnterpriseEnabled,
    signInErrorMesg,
    submitButtonDisabled,
    resetSignInError,
  } = props;

  /** Used local state to keep the submit button disabled state
   * Whenever the form input changed we are enable the submit button again to allow user to submit
   * with new values
   */

  const [isSubmitDisabled, setSubmitDisabled] = useState(submitButtonDisabled);

  useEffect(() => {
    setSubmitDisabled(submitButtonDisabled);
  }, [submitButtonDisabled]);

  const formikRef = useRef();

  const handleOnChange = () => {
    const { values, initialValues } = formikRef.current;
    if (hasSignInError && (initialValues.email !== values.email)) {
      resetSignInError('', false);
    }
  };

  return (
    <div className={cn(s.wrapper, className)}>
      <Formik
        initialValues={{ email: '', password: '' }}
        onSubmit={handleFormSubmit}
        innerRef={formikRef}
      >
        {({
          errors,
          touched,
        }) => (
          <Form
            noValidate
            onChange={handleOnChange}>
            {hasSignInError && (
            <Notification
              color="red"
              text={
                    signInErrorMesg
                    || 'We do not have that e-mail address and password combination on file. Please try again.'
                  }
            />
            )}
            <div className={s.fieldContainer}>
              <Field name="email" validate={validateEmail}>
                {({ field }) => (
                  <FloatingInput
                    {...field}
                    label="Email"
                    required
                    aria-label="Email"
                    data-qm-encrypt
                    errors={touched.email ? [errors.email] : []}
                  />
                )}
              </Field>
            </div>
            <div className={s.fieldContainer}>
              <Field name="password" validate={validatePassword}>
                {({ field }) => (
                  <FloatingPasswordInput
                    {...field}
                    name="password"
                    required
                    aria-label="Password"
                    label="Password"
                    showPasswordButton
                    ignoreInputValidation
                    errors={touched.password ? [errors?.password] : []}
                    data-qm-mask
                  />
                )}
              </Field>
            </div>
            {(showCaptcha && !isRecaptchaEnterpriseEnabled) && (
            <>
              <Recaptcha
                ref={captchaInputRef}
                sitekey={CAPTCHA_KEY}
                onChange={captchaVerifiedCallback}
                onExpired={captchaExpiredCallback}
              />
              {captchaError && <Blurb kind="error">{captchaError}</Blurb>}
            </>
            )}
            <SlotNotificationAuthorable currentPage="login" slot={3} />
            <ThirdPartyScriptLoader
              scriptId="googleRecaptchaEnterprise"
              src={`https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA_ENTERPRISE_KEY}`}
              enabled={isRecaptchaEnterpriseEnabled}
            >
              <Button
                as="button"
                type="submit"
                mode="solid"
                size="large"
                theme="dark"
                className={s.submitButton}
                data-testid="submit-element"
                disabled={isSubmitDisabled}
              >
                {isFetching ? (
                  <Loader data-loader="LoginForm" chroma="light" size="mini" display="inline" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </ThirdPartyScriptLoader>
          </Form>
        )}
      </Formik>
    </div>
  );
}
LoginForm.meta = {
  VERSION: '1.0.0',
};
LoginForm.propTypes = {
  captchaError: PropTypes.string,
  className: PropTypes.string,
  handleFormSubmit: PropTypes.func,
  hasSignInError: PropTypes.bool,
  hasRecaptchaError: PropTypes.bool,
  captchaExpiredCallback: PropTypes.func,
  captchaVerifiedCallback: PropTypes.func,
  captchaInputRef: PropTypes.func,
  showCaptcha: PropTypes.bool,
  isFetching: PropTypes.bool,
  isRecaptchaEnterpriseEnabled: PropTypes.bool,
  isButtonDisabled: PropTypes.bool,
  signInErrorMesg: PropTypes.string,
  submitButtonDisabled: PropTypes.bool,
  resetSignInError: PropTypes.func.isRequired,
};
LoginForm.defaultProps = {
  captchaError: '',
  className: null,
  handleFormSubmit: null,
  hasSignInError: false,
  hasRecaptchaError: false,
  captchaExpiredCallback: null,
  captchaVerifiedCallback: null,
  captchaInputRef: null,
  showCaptcha: false,
  isFetching: false,
  isRecaptchaEnterpriseEnabled: false,
  isButtonDisabled: false,
  signInErrorMesg: '',
  submitButtonDisabled: false,
};

export default LoginForm;
